import React from 'react'
import { AddCircle, MinusCirlce, UserAdd } from 'iconsax-react'

import CopyableStateProvider from '@SonarCX/providers/CopyableStateProvider'

import TimelineSurvey from '../../../molecules/TimelineSurvey'
import TimelineSession from '../../../molecules/TimelineSession'
import UserPartAlert from '../../../molecules/Notification/UserPartAlert'
import UserJoinedAlert from '../../../molecules/Notification/UserJoinedAlert'
import SessionDivider from './SessionDivider'
import Message from '../../../molecules/Message'
import DayDivider from './DayDivider'
import GroupWrapper from './GroupWrapper'
import PeerChatBeginning from './PeerChatBeginning'
import PersonalSpaceBeginning from './PersonalSpaceBeginning'
import TimelineVerificationHeader from '../../../molecules/TimelineVerificationHeader'
import ReasonBlock from '../../../atoms/ReasonBlock'
import Button from '../../../atoms/Button'
import TimelineTaskV2 from '@SonarCX/components/molecules/TimelineTaskV2'

import { checkOnDifferentDays } from '@SonarCX/utilities'
import { checkInGroup } from '@SonarCX/utilities'

const mergeTimelineObjects = ({
  messages,
  notes,
  tasks,
  surveys,
  convo,
  viewing,
  viewingUser,
  canViewNotes,
  endDate,
  session
}) => {
  const filteredTasks = tasks.filter(
    ({ session_ids, concerning_user_id }) =>
      session_ids.includes(convo.session) ||
      (viewing && concerning_user_id === viewingUser)
  )
  const datedTasks = filteredTasks.map(task => {
    const newTask = { ...task }
    newTask.KEY = newTask.id
    newTask.date = new Date(newTask.created_at)
    newTask.TIMELINE = 'DATED_TASK'
    newTask.SEARCH = `${newTask.details} ${newTask.title}`.toLowerCase()
    return newTask
  })
  const msgs = messages
    .filter(msg => {
      if (msg.action === 'survey') {
        return false
      }

      if (
        msg.action === 'session' &&
        ['assigned', 'verified'].includes(msg.type)
      ) {
        return false
      }

      return true
    })
    .map(message => {
      const newMessage = { ...message }
      newMessage.KEY = `${newMessage.id}`
      if (newMessage.correlationId) {
        newMessage.KEY += `+${newMessage.correlationId}`
      }
      // if (!message.content && !message.media.length) {
      if (message.type === 'add') {
        newMessage.TIMELINE = 'ADD'
      } else if (message.type === 'kick') {
        newMessage.TIMELINE = 'KICK'
      } else if (message.type === 'join') {
        newMessage.TIMELINE = 'JOIN'
      } else {
        newMessage.TIMELINE = 'MESSAGE'
        newMessage.SEARCH = `${newMessage.content} ${
          newMessage.sender
        } ${newMessage.tags.join(' ')}`.toLowerCase()
      }
      return newMessage
    })
  const nts = !canViewNotes
    ? []
    : notes.map(note => {
        const newNote = { ...note }
        newNote.KEY = newNote.id
        newNote.TIMELINE = 'NOTE'
        newNote.SEARCH = `${newNote.content} ${
          newNote.sender
        } ${newNote.tags.join(' ')}`.toLowerCase()
        return newNote
      })
  const allSurveys = [
    ...surveys,
    ...messages.filter(msg => msg.action === 'survey')
  ]
  const srvs = allSurveys.map(survey => {
    const newSurvey = { ...survey }
    newSurvey.KEY = newSurvey.id
    newSurvey.date = new Date(newSurvey.stamp ?? newSurvey.date)
    newSurvey.TIMELINE = 'SURVEY'
    newSurvey.SEARCH = ''
    newSurvey.session_id = survey.sessionId ?? survey.session_id
    return newSurvey
  })
  const messageEvents = [
    ...messages.filter(
      msg =>
        msg.action === 'session' && ['assigned', 'verified'].includes(msg.type)
    )
  ].map(event => {
    const newEvent = { ...event }
    newEvent.KEY = newEvent.id
    newEvent.date = new Date(newEvent.stamp ?? newEvent.date)
    newEvent.TIMELINE = 'SESSION'
    newEvent.SEARCH = ''
    newEvent.session_id = event.sessionId ?? event.session_id
    return newEvent
  })
  const end = endDate
    ? [
        {
          KEY: 'session_end',
          date: new Date(endDate),
          TIMELINE: 'END',
          SEARCH: '',
          session_id: session
        }
      ]
    : []
  const combined = [
    ...msgs,
    ...nts,
    ...datedTasks,
    ...srvs,
    ...messageEvents,
    ...end
  ]

  return combined.sort((a, b) => new Date(a.date) - new Date(b.date))
}

const addDayDividers = timeline => {
  const newTimeline = []

  timeline.forEach((ele, i) => {
    const prev = timeline[i - 1]
    if (
      prev?.session_id === ele.session_id &&
      checkOnDifferentDays(prev, ele)
    ) {
      newTimeline.push({
        KEY: `${ele.id}-div`,
        date: ele.date,
        TIMELINE: 'DAY_DIV',
        SEARCH: '',
        session_id: ele.session_id
      })
    }
    newTimeline.push(ele)
  })

  return newTimeline.sort((a, b) => a.date - b.date)
}

const filterTimeline = ({ timeline, filterNotes }) => {
  // Reverse to keep latest if duplicates are found
  const reversed = timeline.reverse()

  // Filter duplicates using map to store seen keys
  const keys = new Map()
  const unique = reversed.filter(item => {
    if (keys.has(item.KEY)) {
      return false
    }

    keys.set(item.KEY, true)
    return true
  })

  // Undo initial reverse
  const normal = unique.reverse()

  if (filterNotes) {
    return normal.filter(event =>
      ['JOIN', 'NOTE', 'END'].includes(event.TIMELINE)
    )
  }

  return normal
}

const searchTimeline = ({ timeline, search }) => {
  return timeline.filter(obj => obj.SEARCH?.includes(search.toLowerCase()))
}

const splitTimeline = ({ timeline, session }) => {
  const old = {}
  const oldStarts = []
  const current = []

  timeline.forEach(event => {
    const id = event.session_id
    if (
      id === session ||
      event.session_ids?.includes(session) // For tasks only
    ) {
      current.push(event)
    } else {
      if (!(id in old)) {
        old[id] = []
        oldStarts.push([id, event.date])
      }
      old[id].push(event)
    }
  })

  oldStarts.sort((a, b) => a[1] - b[1])

  return [old, oldStarts, current]
}

const timelineToElements = ({
  timeline,
  isCurrent,
  allTags,
  allUsers,
  classes,
  targetID,
  targetRef,
  convo,
  lastRead,
  p2p,
  isDirect
}) =>
  timeline.map(e => {
    let name
    switch (e.TIMELINE) {
      case 'DATED_TASK':
        return <TimelineTaskV2 key={e.KEY} task={e} allTags={allTags} />
      case 'SURVEY':
        return <TimelineSurvey key={e.KEY} {...e} />
      case 'SESSION':
        return <TimelineSession key={e.KEY} {...e} />
      case 'JOIN':
        if (e.meta.by_user) {
          if (e.meta.by_user === e.senderFull.id) {
            // dont display own join msg
            name = null
          } else if (allUsers[e.meta.by_user]?.name !== e.senderFull.name) {
            name = allUsers[e.meta.by_user]?.name
          }
        }

        if (e.type === 'part' || e.type === 'kick') {
          return (
            <UserPartAlert
              key={e.id}
              sender={e.sender}
              style={{ justifyContent: 'center' }}
              className={classes.start}
              date={e.date}
              name={name}
            />
          )
        }

        return (
          <UserJoinedAlert
            key={e.id}
            sender={e.sender}
            style={{ justifyContent: 'center' }}
            className={classes.start}
            date={e.date}
            name={name}
          />
        )
      case 'END':
        return <SessionDivider key={e.KEY} date={e.date} isEnd />
      case 'MESSAGE':
        // THESE ARE GENERATED WHEN I PUSH THE PART / KICK EVENT
        if (e.action === 'part' || e.action === 'kick') {
          return (
            <UserPartAlert
              key={e.KEY}
              sender={e.sender}
              style={{ justifyContent: 'center' }}
              className={classes.start}
              date={e.date}
            />
          )
        } else if (e.action === 'join') {
          if (e.meta.by_user === e.senderFull.id) {
            // dont display own join msg
            name = 'own'
          }
          return (
            <UserJoinedAlert
              key={e.KEY}
              sender={e.sender}
              style={{ justifyContent: 'center' }}
              className={classes.start}
              date={e.date}
            />
          )
        } else {
          return (
            <CopyableStateProvider key={e.KEY}>
              <Message
                {...e}
                content={e.content}
                allTags={allTags}
                ref={e.id === targetID ? targetRef : null}
                isPrivateNote={e.TIMELINE === 'NOTE'}
                sessionID={convo.session}
                lastRead={lastRead}
                p2p={p2p}
                above={e.above}
                below={e.below}
                isNew={convo.isNew}
                convo={convo}
                isDirect={isDirect}
                isCurrent={isCurrent}
              />
            </CopyableStateProvider>
          )
        }
      case 'ADD':
        // dont display own join msg
        if (e.meta.by_user === e.senderFull.id) {
          name = 'own'
        } else if (e.meta.by_user) {
          name = allUsers[e.meta.by_user]?.name
        }
        return (
          <UserJoinedAlert
            key={e.id}
            sender={e.sender}
            style={{ justifyContent: 'center' }}
            className={classes.start}
            date={e.date}
            name={name}
          />
        )
      case 'KICK':
        if (e.meta.by_user) {
          name = allUsers[e.meta.by_user]?.name
        }
        return (
          <UserPartAlert
            key={e.id}
            sender={e.sender}
            style={{ justifyContent: 'center' }}
            className={classes.start}
            date={e.date}
            name={name}
          />
        )
      case 'DAY_DIV':
        return <DayDivider key={e.KEY} date={e.date} />
      default:
        return (
          <CopyableStateProvider key={e.KEY}>
            <Message
              {...e}
              content={e.content}
              allTags={allTags}
              ref={e.id === targetID ? targetRef : null}
              isPrivateNote={e.TIMELINE === 'NOTE'}
              sessionID={convo.session}
              lastRead={lastRead}
              p2p={p2p}
              above={e.above}
              below={e.below}
              isNew={convo.isNew}
              convo={convo}
              isDirect={isDirect}
              isCurrent={isCurrent}
            />
          </CopyableStateProvider>
        )
    }
  })

const hasEvents = timeline => {
  const onlyEvents = timeline.filter(
    event => !['JOIN', 'END'].includes(event.TIMELINE)
  )
  return onlyEvents.length > 0
}

export const createTimeline = ({
  messages = [],
  notes = [],
  tasks = [],
  surveys = [],
  convo,
  viewing,
  viewingUser,
  canViewNotes,
  endDate,
  session,
  filterNotes,
  search,
  allTags,
  allUsers,
  classes,
  targetID,
  targetRef,
  lastRead,
  p2p,
  isDirect
}) => {
  const combined = mergeTimelineObjects({
    messages,
    notes,
    tasks: tasks.filter(
      t => !t.deleted_at && t.concerning_user_id && !t.closed_at
    ),
    surveys,
    convo,
    viewing,
    viewingUser,
    canViewNotes,
    endDate,
    session
  })

  let timeComparison = [...combined]

  const ids = new Set()
  combined.forEach(item => {
    if (item.session_id) {
      ids.add(item.session_id)
    }
  })

  const hasMultipleSessions = ids.size > 1

  timeComparison.forEach((ele, i) => {
    const prev = timeComparison[i - 1]
    const next = timeComparison[i + 1]

    ele.above = checkInGroup(prev, ele)
    ele.below = checkInGroup(ele, next)
  })

  const divided = addDayDividers(timeComparison)

  let filtered = filterTimeline({ timeline: divided, filterNotes })

  if (search?.length > 0) {
    filtered = searchTimeline({ timeline: filtered, search })
  }

  const elementProps = {
    allTags,
    allUsers,
    classes,
    targetID,
    targetRef,
    convo,
    lastRead,
    p2p,
    isDirect
  }

  if (isDirect) {
    return [
      [],
      [],
      timelineToElements({
        timeline: filtered,
        isCurrent: true,
        ...elementProps
      }),
      hasMultipleSessions
    ]
  }

  const [old, oldStarts, current] = splitTimeline({
    timeline: filtered,
    session
  })

  const oldTimelines = {}
  Object.keys(old).forEach(id => {
    oldTimelines[id] = hasEvents(old[id])
      ? timelineToElements({
          timeline: old[id],
          isCurrent: false,
          ...elementProps
        })
      : null
  })

  return [
    oldTimelines,
    oldStarts,
    timelineToElements({
      timeline: current,
      isCurrent: true,
      ...elementProps
    }),
    hasMultipleSessions
  ]
}

export const getShowableOld = ({
  allTags,
  classes,
  collapsed,
  combinedOld,
  oldTimelines,
  toggleCollapse
}) => {
  const showable = []
  combinedOld.forEach(([id, date, extra]) => {
    if (id === 'TASK') {
      showable.push(
        <GroupWrapper key={extra.id} noColor>
          <TimelineTaskV2 task={extra} allTags={allTags} />
        </GroupWrapper>
      )
      return
    }

    if (!oldTimelines[id]) {
      return
    }

    // Add end and start blocks
    const Icon = collapsed.includes(id) ? AddCircle : MinusCirlce
    showable.push(
      <GroupWrapper key={id + '-start'} start>
        <SessionDivider date={date}>
          <div className={classes.iconHolder}>
            <Icon onClick={toggleCollapse(id)} className={classes.icon} />
          </div>
        </SessionDivider>
      </GroupWrapper>
    )

    if (!collapsed.includes(id)) {
      showable.push(
        ...oldTimelines[id].map((x, i) => (
          <GroupWrapper key={x.key ?? `${id}-${i}`}>{x}</GroupWrapper>
        ))
      )
    }

    showable.push(
      <GroupWrapper key={id + '-end'} end>
        <div />
      </GroupWrapper>
    )
  })

  return showable
}

const getChatInfo = ({ convo, allUsers, startDate, isDirect }) => {
  if (!convo) {
    return
  }

  const { name, isGroup, user, users, created_by, messages } = convo
  const metaUser = messages[0]?.meta?.by_user

  const group = Boolean(isGroup)

  const createdUser = created_by
    ? allUsers[created_by]?.name
    : messages[0]?.meta.by_user
    ? allUsers[metaUser]?.name
    : null

  const isPersonalSpace = users?.every(i => users[0] === i) && !group

  if (group) {
    return (
      <SessionDivider
        key='session_start'
        date={startDate}
        group
        createdUser={createdUser}
      />
    )
  } else if (isDirect && isPersonalSpace) {
    return (
      <PersonalSpaceBeginning key='session_start' name={name} user={user} />
    )
  } else if (isDirect) {
    return <PeerChatBeginning key='session_start' name={name} user={user} />
  } else {
    return (
      <SessionDivider
        key='session_start'
        date={startDate}
        createdUser={createdUser}
        convo={convo}
      />
    )
  }
}

export const getCurrentTimeline = ({
  empty,
  doneLoading,
  hasMultipleSessions,
  isDirect,
  verified,
  allUsers,
  classes,
  convo,
  currentTimeline,
  ownerDeleted,
  session,
  setEditSessionUsers,
  startDate
}) => {
  const current = []

  if (!empty && (doneLoading || hasMultipleSessions)) {
    current.push(
      <GroupWrapper
        key={session + '-start'}
        id='timeline-start'
        noColor={isDirect}
        start
      >
        {!isDirect && !verified && <TimelineVerificationHeader />}

        <div key={session + '-start'}>
          {getChatInfo({ convo, allUsers, startDate, isDirect })}
        </div>
      </GroupWrapper>
    )
  }

  if (!isDirect && (doneLoading || hasMultipleSessions)) {
    current.push(
      <GroupWrapper key={session + '-reason'}>
        <ReasonBlock id={session} />
      </GroupWrapper>
    )
  }

  if (!isDirect && !ownerDeleted && (doneLoading || hasMultipleSessions)) {
    current.push(
      <GroupWrapper key={session + '-add'}>
        <div key='add_participant' className={classes.addParticipantButton}>
          <Button onClick={() => setEditSessionUsers(true)}>
            <UserAdd className='icon' />

            <div className='text'>ADD/REMOVE PARTICIPANTS</div>
          </Button>
        </div>
      </GroupWrapper>
    )
  }

  current.push(
    ...currentTimeline.map(x => (
      <GroupWrapper key={x.key} noColor={isDirect}>
        {x}
      </GroupWrapper>
    ))
  )

  current.push(
    <GroupWrapper
      key={session + '-end'}
      noColor={isDirect}
      end={convo.closed}
      className={convo.closed && classes.closed}
    >
      <div style={{ height: '1rem' }} />
    </GroupWrapper>
  )

  return current
}
